import { Ban, Check, ChevronRight, FolderGit2, Hand, Radar, ScrollText, SlidersHorizontal } from '@orgus/ui/icons';
import type { ComponentType } from 'react';
import { Link } from 'react-router';
import type { SettingsSection } from '../registry.ts';
import { PoliciesView } from '../../approvals/PoliciesView.tsx';
import { AnswererPanel } from '../../approvals/AnswererPanel.tsx';

const DECISIONS: { icon: ComponentType<{ className?: string }>; label: string; text: string; tone: string }[] = [
  { icon: Check, label: 'Allow', text: 'Runs without asking', tone: 'text-fg-subtle' },
  { icon: Hand, label: 'Ask a person', text: 'Waits in your Inbox', tone: 'text-status-attention' },
  { icon: Ban, label: 'Deny', text: 'Told no, and why', tone: 'text-status-danger' },
];

const RELATED: { to: string; icon: ComponentType<{ className?: string }>; title: string }[] = [
  { to: '/settings/launch', icon: SlidersHorizontal, title: 'Permission mode' },
  { to: '/settings/session-control', icon: Radar, title: 'Every session on a machine' },
  { to: '/projects', icon: FolderGit2, title: 'Per project rules live on each project' },
];

/**
 * What a rule can decide, as three quiet tiles, and the related settings as links (each project's own
 * autonomy is on Projects, in its menu). The first
 * matching rule decides; with none, the defaults do (said once, in the tiles' accessible name).
 */
function HowItWorks() {
  return (
    <div className="flex flex-col gap-3">
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-3" aria-label="What a rule can decide. The first rule that matches decides; with none, the built-in defaults do.">
        {DECISIONS.map((d) => (
          <li key={d.label} className="motion-arrive flex items-center gap-3 rounded-card border border-border bg-surface px-4 py-3 shadow-elevation-flat surface-sheen">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-active" aria-hidden>
              <d.icon className={`size-4 ${d.tone}`} />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-ui font-medium text-fg">{d.label}</span>
              <span className="truncate text-caption text-fg-subtle">{d.text}</span>
            </span>
          </li>
        ))}
      </ul>
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1" aria-label="Related settings">
        {RELATED.map((r) => (
          <li key={r.to}>
            <Link to={r.to} className="inline-flex h-control-sm items-center gap-2 rounded-control text-caption font-medium text-fg-muted hover:text-fg">
              <r.icon className="size-4 text-fg-subtle" aria-hidden />
              {r.title}
              <ChevronRight className="size-3.5 text-fg-faint" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ApprovalRules() {
  return (
    <div className="flex flex-col gap-6">
      <AnswererPanel />
      <HowItWorks />
      <PoliciesView />
    </div>
  );
}

const section: SettingsSection = {
  id: 'policies',
  title: 'Approval rules',
  description: 'What agents may do without asking you, by tool, command or file path.',
  order: 10,
  group: 'agents',
  keywords: ['who answers', 'orgus answers', 'not asked', 'auto-approve', 'auto approve', 'approvals', 'permissions', 'permission level', 'allow', 'deny', 'ask', 'policy', 'policies', 'rules', 'hitl'],
  icon: ScrollText,
  Component: ApprovalRules,
};
export default section;
